import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Lucide from "lucide-react";
import { Sparkles } from "lucide-react";

// Render a single floating bubble that gently drifts upward.
function Bubble({ bubble, color, accent, onPop, testId, lowStim }) {
  const Icon = (bubble.icon && Lucide[bubble.icon]) || Sparkles;
  // randomized motion params per bubble — stable for the lifetime of this bubble
  const [seed] = React.useState(() => ({
    drift: 20 + Math.random() * 60,
    duration: 9 + Math.random() * 7,
    delay: -Math.random() * 6,
    scale: 0.9 + Math.random() * 0.25,
  }));

  const motionProps = lowStim
    ? { animate: { y: 0, x: 0 }, transition: { duration: 0 } }
    : {
        animate: {
          y: [-12, 12, -12],
          x: [-seed.drift / 2, seed.drift / 2, -seed.drift / 2],
        },
        transition: {
          duration: seed.duration,
          repeat: Infinity,
          ease: "easeInOut",
          delay: seed.delay,
        },
      };

  return (
    <motion.button
      type="button"
      data-testid={testId}
      onClick={() => onPop(bubble)}
      whileTap={{ scale: 0.4, opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="group relative shrink-0 select-none"
      style={{ width: 168, height: 168 }}
      aria-label={`Pop ${bubble.bubble_name}`}
    >
      <motion.div
        {...motionProps}
        className="relative rounded-full flex flex-col items-center justify-center text-center px-3"
        style={{
          width: "100%", height: "100%",
          background: `radial-gradient(circle at 32% 28%, #ffffff 0%, ${accent} 38%, ${color} 100%)`,
          boxShadow: `inset 6px 8px 18px rgba(255,255,255,0.55), inset -8px -10px 22px rgba(0,0,0,0.18), 0 18px 30px -18px ${color}66`,
          transform: `scale(${seed.scale})`,
        }}
      >
        {/* highlight */}
        <span aria-hidden className="absolute top-3 left-5 w-10 h-7 rounded-full bg-white/70 blur-md" />
        <Icon size={30} className="text-white drop-shadow-sm" strokeWidth={2} />
        <div className="mt-2 display font-bold text-white text-sm sm:text-base leading-tight px-2 break-words" style={{ textShadow: "0 1px 1px rgba(0,0,0,0.25)" }}>
          {bubble.bubble_name}
        </div>
      </motion.div>
    </motion.button>
  );
}

export default function BubbleField({ bubbles, color, accent, onPop, lowStim, testIdFor, perBubbleColor }) {
  const [popped, setPopped] = React.useState({});

  const handlePop = (b) => {
    setPopped((p) => ({ ...p, [b.id]: true }));
    onPop(b);
  };

  const visible = bubbles.filter((b) => !popped[b.id]);

  return (
    <div className="relative w-full">
      <div className="flex flex-wrap gap-6 md:gap-8 justify-center py-6">
        <AnimatePresence>
          {visible.map((b) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 30, scale: 0.5 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.3 }}
              transition={{ duration: 0.4 }}
            >
              <Bubble
                bubble={b}
                color={perBubbleColor && b._color ? b._color : color}
                accent={perBubbleColor && b._accent ? b._accent : accent}
                onPop={handlePop}
                testId={testIdFor ? testIdFor(b.id) : `bubble-${b.id}`}
                lowStim={lowStim}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      {visible.length === 0 && bubbles.length > 0 && (
        <div className="text-center py-12">
          <button
            type="button"
            onClick={() => setPopped({})}
            className="sw-pill h-12 px-6"
          >
            Bring the bubbles back
          </button>
        </div>
      )}
    </div>
  );
}
